import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { InventoryItemModal } from '../components/InventoryItemModal';
import { StockAdjustmentModal } from '../components/StockAdjustmentModal';
import type { StockAdjustmentTarget } from '../components/StockAdjustmentModal';
import {
  inventoryCategoriesApi,
  inventoryItemTypesApi,
  inventoryItemsApi,
  unitsOfMeasureApi,
} from '../inventoryApi';
import type { InventoryItemDetail, InventoryItemListItem } from '../inventoryTypes';
import { ITEM_KIND_LABEL } from '../inventoryTypes';

/**
 * The item catalog — the types of thing the organization stocks.
 *
 * **The anchor is the SKU, not the name**, which is the one place this product's anchor rule
 * yields. An anchor is normally whatever a reader calls the row, and a code titles every row
 * alike — but two live items legitimately share a name here (gloves in three sizes are three
 * items of one governed name), so anchoring on the name would title those three rows
 * identically. The SKU cannot: it is unique permanently, and it is what the shelf label and the
 * requisition quote. The name keeps its own sortable column immediately beside it.
 */
export function InventoryItemsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<InventoryItemDetail | null>(null);
  const [creating, setCreating] = useState(false);

  // The item a count is being recorded against, straight off a registration the reader ticked
  // "set the opening count" on. Null closes the form.
  const [adjusting, setAdjusting] = useState<StockAdjustmentTarget | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    item: InventoryItemListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    An item cannot be created without a category, an item type and a unit — all three are
    required on the form — so the page asks whether each catalog holds anything and the shell
    states what is missing before the reader opens a form they could not submit.

    Seeded TRUE: until the three feeds answer, the assumption is that the module is set up,
    because flashing "set up categories first" on every load of a working module is worse than
    a banner arriving a moment late. A failed feed leaves it true for the same reason — a
    request that did not come back is not evidence that a catalog is empty.

    No permission guard: `inventory-items.read` implies all three catalogs' reads, so any
    reader of this list holds them.
  */
  const [ready, setReady] = useState({ categories: true, itemTypes: true, units: true });

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      inventoryCategoriesApi.options(controller.signal),
      inventoryItemTypesApi.options(controller.signal),
      unitsOfMeasureApi.options(controller.signal),
    ])
      .then(([categories, itemTypes, units]) => {
        if (controller.signal.aborted) return;

        setReady({
          categories: categories.length > 0,
          itemTypes: itemTypes.length > 0,
          units: units.length > 0,
        });
      })
      .catch(() => undefined);

    return () => controller.abort();

    // Re-asked after a write, so filling the last empty catalog clears the banner without a
    // reload — the reader fixed it, and the page should agree.
  }, [reloadKey]);

  const fetchItems = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      inventoryItemsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          category: query.facets.category?.join(','),
          kind: query.facets.kind?.join(','),
          tracking: query.facets.tracking?.join(','),
        },
        signal,
      ),
    [],
  );

  /*
    The list row carries only what a list needs, so opening the editor fetches the whole record
    first. A form seeded from the list row would silently drop everything the list does not show
    — the manufacturer, the licensing model, the useful life — and saving would clear them.
  */
  const openEditor = useCallback(async (item: InventoryItemListItem) => {
    setError(null);

    try {
      setEditing(await inventoryItemsApi.get(item.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not open this item.', 0));
    }
  }, []);

  const rowActions = useCallback(
    (item: InventoryItemListItem): MenuItem[] => [
      {
        key: `edit-${item.id}`,
        label: 'Edit',
        permission: 'inventory-items.update',
        onClick: () => void openEditor(item),
      },
      {
        key: `restore-${item.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'inventory-items.delete',
        disabledReason: item.isActive ? 'This item is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', item }),
      },
    ],
    [openEditor],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await inventoryItemsApi.deactivate(confirming.item.id);
      else await inventoryItemsApi.activate(confirming.item.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<InventoryItemListItem>
        module="inventory-items"
        breadcrumb={[
          'Logistics',
          'Inventory',
          /* The breadcrumb IS the back-nav, and a page one level inside another has no other
             way out — so Settings links, per every settings-nested module here. */
          { label: 'Settings', to: '/inventory/settings' },
          'Items',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'category', label: 'Category' },
          { key: 'kind', label: 'Kind' },
          { key: 'tracking', label: 'Tracked' },
        ]}
        /*
          All three are REQUIRED on the item form, which is what earns them a place here — an
          optional picker with nothing in it is a field the reader skips, and a banner about one
          would be noise on every fresh database.
        */
        prerequisites={[
          {
            label: 'categories',
            met: ready.categories,
            to: '/inventory/settings/categories',
            permission: 'inventory-categories.read',
          },
          {
            label: 'item types',
            met: ready.itemTypes,
            to: '/inventory/settings/item-types',
            permission: 'inventory-item-types.read',
          },
          {
            label: 'units of measure',
            met: ready.units,
            to: '/inventory/settings/units',
            permission: 'units-of-measure.read',
          },
        ]}
        auditEntityTypes={['InventoryItem']}
        rowKey={(item) => item.id}
        fetch={fetchItems}
        reloadKey={reloadKey}
        searchPlaceholder="Search SKU, name, brand or model"
        emptyMessage="No items yet. Add the things this organization stocks."
        onCreate={() => setCreating(true)}
        createPermission="inventory-items.create"
        onRowOpen={(item) => navigate(`/inventory/settings/items/${item.id}`)}
        openPermission="inventory-items.read"
        rowActions={rowActions}
        onDelete={(item) => setConfirming({ kind: 'deactivate', item })}
        deletePermission="inventory-items.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(item) => (item.isActive ? null : 'This item is already deleted.')}
        onClearData={() => inventoryItemsApi.clear()}
        clearDataPermission="inventory-items.clear-data"
        otherInfo={(item) => item}
      />

      {creating || editing !== null ? (
        <InventoryItemModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
          /*
            STRAIGHT INTO A DELIVERY, when the reader ticked for it — the same hand-off Stock
            Levels makes, because this page registers items too and a reader here should not
            have to go and find the row they just created.

            `onHand` is 0 because it is: the item exists and nothing has been received against
            it. Supplying this handler is also what DRAWS the checkbox.
          */
          onAdjust={(item) =>
            setAdjusting({
              id: item.id,
              sku: item.sku,
              name: item.name,
              unitCode: item.unitCode,
              inventoryCategoryName: item.inventoryCategoryName,
              onHand: 0,
            })
          }
        />
      ) : null}

      <StockAdjustmentModal
        open
        target={adjusting}
        onClose={() => setAdjusting(null)}
        onSaved={() => {
          setAdjusting(null);
          refresh();
        }}
      />

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete item' : 'Restore item'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring puts something back.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`inventory-item-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.item.sku}" stops being offered on stock forms.`
                  : `"${confirming.item.sku}" is offered again on stock forms.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  Its SKU stays held. A SKU is never reissued — a label, a requisition and a count
                  sheet all quote it — so nothing else can take <strong>{confirming.item.sku}</strong>,
                  and restoring this item cannot collide with anything.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted item can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<InventoryItemListItem>[] = [
  {
    key: 'sku',
    header: 'SKU',
    width: 170,
    sortable: true,
    render: (item) => <TextCell value={item.sku} mono />,
  },
  {
    /*
      NOT gated, and that is deliberate rather than an oversight. `inventory-items.read` implies
      the three catalogs' reads UNSCOPED, so every reader of this list already holds them — a
      gate here could never hide anything, which is the defect CLAUDE.md names on the personnel
      list's plantilla columns. The facility list's Department column IS gated, because that
      implied read is scoped to the write actions.
    */
    key: 'name',
    header: 'Name',
    width: 260,
    flex: true,
    sortable: true,
    summary: true,
    render: (item) => <TextCell value={item.name} />,
  },
  {
    key: 'category',
    header: 'Category',
    width: 180,
    sortable: true,
    summary: true,
    render: (item) => <TextCell value={item.inventoryCategoryName} />,
  },
  {
    key: 'kind',
    header: 'Kind',
    width: 140,
    sortable: true,
    summary: true,
    render: (item) => <TextCell value={ITEM_KIND_LABEL[item.kind]} />,
  },
  {
    key: 'unitCode',
    header: 'Unit',
    width: 90,
    render: (item) => <TextCell value={item.unitCode} mono />,
  },
  /*
    NO unit-cost column — the catalog row stopped carrying one on 20 Sep 2026. A price is a fact
    about the STOCK, so it lives on the lot and on the unit, and Stock Levels' Investment column
    is what totals them. Dropping the column took its sort key with it: the server's allow-list
    no longer names `unitCost`, and a header that silently fell back to the default sort would
    read as a broken control.
  */
  {
    key: 'reorderLevel',
    header: 'Reorder at',
    width: 110,
    align: 'right',
    sortable: true,
    render: (item) => <TextCell value={String(item.reorderLevel)} mono numeric />,
  },
  {
    key: 'tracking',
    header: 'Tracked',
    width: 120,
    render: (item) => <TextCell value={item.tracksSerial ? 'By serial' : 'By quantity'} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (item) =>
      item.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
